import { Box, Stack } from '@mui/material';
import type { BoxProps } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import React from 'react';

import { ButterCup, Neutrals, SemanticError, SemanticSuccess } from '../../color-palette/colors';

import { dockActionsSx, dockMessageSx, dockRiseSx, dockRootSx, dockSlotSx } from './dockStyles';
import type { DockActionsLayout, DockEntrance, DockPlacement, StickyActionBarLayout, StickyActionBarTone } from './types';

export type { DockActionsLayout, DockEntrance, DockPlacement, StickyActionBarLayout, StickyActionBarTone } from './types';

interface StickyActionBarBaseProps extends Omit<BoxProps, 'children'> {
  message?: React.ReactNode;
  actions?: React.ReactNode;
  tone?: StickyActionBarTone;
}

interface DockOnlyProps {
  primary?: React.ReactElement;
  secondary?: React.ReactElement;
  tertiary?: React.ReactElement;
  start?: React.ReactElement;
  actionsLayout?: DockActionsLayout;
  placement?: DockPlacement;
  announce?: boolean;
  entrance?: DockEntrance;
}

type Without<T> = { [K in keyof T]?: never };

export interface StickyActionBarCardProps extends StickyActionBarBaseProps, Without<DockOnlyProps> {
  layout?: Extract<StickyActionBarLayout, 'card'>;
}

export interface StickyActionBarDockProps extends StickyActionBarBaseProps, DockOnlyProps {
  layout: Extract<StickyActionBarLayout, 'dock'>;
}

export type StickyActionBarProps = StickyActionBarCardProps | StickyActionBarDockProps;

const CARD_TONE: Readonly<Record<StickyActionBarTone, { border: string; background: string }>> = {
  neutral: { border: Neutrals[100], background: Neutrals[10] ?? Neutrals[50] },
  warning: { border: ButterCup[500], background: ButterCup[50] },
  success: { border: SemanticSuccess[100], background: SemanticSuccess[50] },
  error: { border: SemanticError[100], background: SemanticError[50] },
};

const isBare = (message: React.ReactNode): boolean => message === null || message === undefined || message === false;

const cardRootSx = (theme: Theme): SystemStyleObject<Theme> => ({
  position: 'sticky',
  insetBlockEnd: 0,
  isolation: 'isolate',
  zIndex: theme.zIndex.mobileStepper,
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row' },
  alignItems: { xs: 'stretch', sm: 'center' },
  justifyContent: 'space-between',
  gap: 2,
  transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow'], {
    duration: theme.transitions.duration.shortest,
  }),
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
});

const cardBareSx = (theme: Theme): SystemStyleObject<Theme> => ({
  paddingBlock: 2,
  backgroundColor: theme.palette.background.paper,
  borderBlockStart: '1px solid',
  borderColor: Neutrals[100],
});

const cardToneSx =
  (tone: StickyActionBarTone) =>
  (theme: Theme): SystemStyleObject<Theme> => ({
    paddingInline: 4,
    paddingBlockStart: 3,
    paddingBlockEnd: `max(env(safe-area-inset-bottom), ${theme.spacing(3)})`,
    borderRadius: 2.5,
    border: '1px solid',
    borderColor: CARD_TONE[tone].border,
    backgroundColor: CARD_TONE[tone].background,
    boxShadow: theme.customShadows.cardElevated,
  });

const CARD_TONE_SX: Readonly<Record<StickyActionBarTone, (theme: Theme) => SystemStyleObject<Theme>>> = {
  neutral: cardToneSx('neutral'),
  warning: cardToneSx('warning'),
  success: cardToneSx('success'),
  error: cardToneSx('error'),
};

const CARD_MESSAGE_SX: SystemStyleObject<Theme> = { minWidth: 0, overflowWrap: 'anywhere' };

const CARD_ACTIONS_SX = {
  bare: { flexShrink: 0, paddingBlockStart: 2 },
  framed: { flexShrink: 0, paddingBlockStart: 0 },
} as const;

type CardBarProps = Omit<StickyActionBarCardProps, 'layout' | keyof DockOnlyProps>;

const CardBar = React.forwardRef<HTMLDivElement, CardBarProps>(({ message, actions, tone = 'warning', sx, ...rest }, ref) => {
  const bare = isBare(message);

  return (
    <Box
      ref={ref}
      data-action-dock=""
      {...rest}
      sx={[cardRootSx, bare ? cardBareSx : CARD_TONE_SX[tone], ...(Array.isArray(sx) ? sx : [sx])]}
    >
      <Box role="status" aria-live="polite" sx={CARD_MESSAGE_SX}>
        {message}
      </Box>
      <Stack direction="row" spacing={2} justifyContent="flex-end" sx={bare ? CARD_ACTIONS_SX.bare : CARD_ACTIONS_SX.framed}>
        {actions}
      </Stack>
    </Box>
  );
});

CardBar.displayName = 'StickyActionBar.Card';

type DockBarProps = Omit<StickyActionBarDockProps, 'layout'>;

const DockMessage: React.FC<{ message: React.ReactNode; tone: StickyActionBarTone; announce: boolean }> = ({
  message,
  tone,
  announce,
}) => {
  if (announce) {
    return (
      <Box role="status" aria-live="polite" sx={dockMessageSx(tone)}>
        {message}
      </Box>
    );
  }
  if (isBare(message)) return null;
  return <Box sx={dockMessageSx(tone)}>{message}</Box>;
};

const DockBar = React.forwardRef<HTMLDivElement, DockBarProps>(
  (
    {
      message,
      actions,
      tone = 'neutral',
      primary,
      secondary,
      tertiary,
      start,
      actionsLayout = 'stack',
      placement = 'page',
      announce = true,
      entrance = 'none',
      sx,
      ...rest
    },
    ref,
  ) => {
    const hasActions = !isBare(actions) || Boolean(start) || Boolean(primary) || Boolean(secondary) || Boolean(tertiary);
    return (
      <Box
        ref={ref}
        data-action-dock=""
        {...rest}
        sx={[dockRootSx(tone, placement), entrance === 'rise' && dockRiseSx, ...(Array.isArray(sx) ? sx : [sx])]}
      >
        <DockMessage message={message} tone={tone} announce={announce} />
        {hasActions && (
          <Box sx={dockActionsSx(actionsLayout)}>
            {start && <Box sx={dockSlotSx.start}>{start}</Box>}
            {!isBare(actions) && <Box sx={dockSlotSx.legacy(actionsLayout)}>{actions}</Box>}
            {tertiary && <Box sx={dockSlotSx.tertiary(actionsLayout)}>{tertiary}</Box>}
            {secondary && <Box sx={dockSlotSx.secondary(actionsLayout)}>{secondary}</Box>}
            {primary && <Box sx={dockSlotSx.primary(actionsLayout)}>{primary}</Box>}
          </Box>
        )}
      </Box>
    );
  },
);

DockBar.displayName = 'StickyActionBar.Dock';

export const StickyActionBar = React.forwardRef<HTMLDivElement, StickyActionBarProps>((props, ref) => {
  if (props.layout === 'dock') {
    const { layout: _layout, ...dock } = props;
    return <DockBar ref={ref} {...dock} />;
  }
  const { layout: _layout, ...card } = props;
  return <CardBar ref={ref} {...card} />;
});

StickyActionBar.displayName = 'StickyActionBar';
